import React, { useState, useMemo, useEffect } from 'react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import type { Payment } from '../../types/payment.types';
import { usePayments } from '../../hooks/usePayments';
import {
  useMarkPaymentPaid,
  useBulkMarkPaymentsPaid,
  useUpdateOverduePayments,
} from '../../hooks/usePaymentMutations';
import { usePaymentFiltering } from '../../hooks/usePaymentFiltering';
import { usePaymentExport } from '../../hooks/usePaymentExport';
import { calculatePaymentMetrics } from '../../services/paymentCalculations.service';
import { PaymentScreenHeader } from '../header/PaymentScreenHeader';
import { PaymentMetrics } from '../metrics/PaymentMetrics';
import { PaymentQuickFilters } from '../filters/PaymentQuickFilters';
import { PaymentFilters } from '../filters/PaymentFilters';
import { AppliedFiltersBanner } from '../filters/AppliedFiltersBanner';
import { PaymentBulkActions } from '../table/PaymentBulkActions';
import { PaymentsTable } from '../table/PaymentsTable';
import { AddPaymentModal } from '../modal/AddPaymentModal';
import { PaymentDetailDrawer } from '../drawer/PaymentDetailDrawer';

export const PaymentsScreen: React.FC = () => {
  const { userCountry } = useAuth();
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);

  // Load payments from Supabase
  const { data: allPayments = [], isLoading, error, refetch } = usePayments();

  // Mutation hooks
  const markPaidMutation = useMarkPaymentPaid();
  const bulkMarkPaidMutation = useBulkMarkPaymentsPaid();
  const updateOverdueMutation = useUpdateOverduePayments();

  // Check for overdue payments on load
  useEffect(() => {
    if (allPayments.length > 0) {
      const today = new Date();
      const overduePayments = allPayments.filter(
        (payment) =>
          payment.status === 'pending' &&
          payment.dueDate &&
          new Date(payment.dueDate) < today
      );

      if (overduePayments.length > 0) {
        updateOverdueMutation.mutate(overduePayments.map((p) => p.id));
      }
    }
  }, [allPayments]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally only re-run when allPayments changes

  // Filtering hook
  const {
    activeFilter,
    setActiveFilter,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    payments,
  } = usePaymentFiltering(allPayments);

  // Metrics computation
  const { totalDue, paidThisMonth, pendingApprovals, missingInvoices } = useMemo(
    () => calculatePaymentMetrics(allPayments),
    [allPayments]
  );

  // Export hook
  const { isExporting, handleExport, handleBulkExportSelected } = usePaymentExport();

  // Selection handlers
  const handleSelectPayment = (paymentId: string) => {
    setSelectedPayments((prev) =>
      prev.includes(paymentId) ? prev.filter((id) => id !== paymentId) : [...prev, paymentId]
    );
  };

  const handleSelectAll = () => {
    setSelectedPayments(
      selectedPayments.length === payments.length ? [] : payments.map((p) => p.id)
    );
  };

  // Mark single payment as paid
  const handleMarkPaid = (paymentId: string) => {
    markPaidMutation.mutate(paymentId);
  };

  // Bulk mark as paid
  const handleBulkMarkPaid = () => {
    if (selectedPayments.length === 0) return;
    bulkMarkPaidMutation.mutate(selectedPayments, {
      onSuccess: () => setSelectedPayments([]),
    });
  };

  // Trigger export for all filtered
  const triggerExport = () => {
    handleExport(payments, dateRange, userCountry);
  };

  // Trigger export for selected
  const triggerBulkExportSelected = () => {
    handleBulkExportSelected(payments, selectedPayments, dateRange, userCountry, () => {
      setSelectedPayments([]);
    });
  };

  return (
    <TooltipProvider>
      <div className="p-6 space-y-6">
        {/* Header */}
        <PaymentScreenHeader
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(!showFilters)}
          onExport={triggerExport}
          isExporting={isExporting}
          onOpenAddPayment={() => setShowAddModal(true)}
        />

        {/* Metrics Cards */}
        <PaymentMetrics
          totalDue={totalDue}
          paidThisMonth={paidThisMonth}
          pendingApprovals={pendingApprovals}
          missingInvoices={missingInvoices}
        />

        {/* Quick Filters */}
        <PaymentQuickFilters
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />

        {/* Filters Sidebar */}
        {showFilters && (
          <PaymentFilters
            onClose={() => setShowFilters(false)}
            onApplyFilters={handleApplyFilters}
          />
        )}

        {/* Applied Filters Banner */}
        <AppliedFiltersBanner
          appliedFilters={appliedFilters}
          onClearFilters={handleClearFilters}
        />

        {/* Bulk Actions Bar */}
        {selectedPayments.length > 0 && (
          <PaymentBulkActions
            selectedCount={selectedPayments.length}
            bulkMarkingPaid={bulkMarkPaidMutation.isPending}
            onBulkMarkPaid={handleBulkMarkPaid}
            onBulkExport={triggerBulkExportSelected}
            onSendReminder={() => {
              /* Send reminder - stub */
            }}
          />
        )}

        {/* Payments Table */}
        <PaymentsTable
          payments={payments}
          isLoading={isLoading}
          error={error}
          selectedPayments={selectedPayments}
          markingPaidIds={
            markPaidMutation.isPending
              ? [markPaidMutation.variables as string].filter(Boolean)
              : []
          }
          userCountry={userCountry}
          onSelectPayment={handleSelectPayment}
          onSelectAll={handleSelectAll}
          onMarkPaid={handleMarkPaid}
          onViewPayment={setSelectedPayment}
        />

        {/* Add Payment Modal */}
        <AddPaymentModal
          key={showAddModal ? 'open' : 'closed'}
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => refetch()}
        />

        {/* Payment Detail Drawer */}
        {selectedPayment && (
          <PaymentDetailDrawer
            key={selectedPayment.id}
            payment={selectedPayment}
            onClose={() => setSelectedPayment(null)}
          />
        )}
      </div>
    </TooltipProvider>
  );
};

export default PaymentsScreen;
